import { useEffect, useId, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ArrowDown,
  ArrowUpRight,
  Check,
  HelpCircle,
  Layers,
  LayoutGrid,
  Search,
  X,
} from "lucide-react";
import V2BusyFrame, { V2Skeleton } from "./V2BusyFrame";
import V2PlayerAvatar from "./V2PlayerAvatar";
import { TREND_METRICS } from "./v2OverviewConstants";
import {
  FOLDED_AXIS_TICKS,
  FOLDED_ZONE_BANDS,
  foldedLeftPercent,
  formatScore,
  matchesPosition,
  resolveFoldedZone,
  V2_POSITIONS,
  V2_ZONE_META,
} from "./v2OverviewUtils";

const DOW_KEYS = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];

function periodLength(labels) {
  return labels?.length || 0;
}

function trendLabels(period, t, labels) {
  if (!labels?.length) return [];
  if (period === "week") {
    return labels.map((iso) => {
      const date = new Date(`${iso}T00:00:00`);
      if (Number.isNaN(date.getTime())) return iso;
      return t("v2.overview.trends.week_day", {
        day: t(`v2.overview.trends.dow.${DOW_KEYS[date.getDay()]}`),
        date: date.getDate(),
      });
    });
  }
  return labels.map((label) => {
    const week = String(label).split("-W")[1];
    return t("v2.overview.trends.week_unit", { n: Number(week) || label });
  });
}

function getSerie(player, period, metric, trends) {
  const stored = trends?.series?.[String(player.id)]?.[metric.id];
  if (Array.isArray(stored) && stored.length) {
    return stored.map((value) => (Number.isFinite(value) ? value : null));
  }
  const count = periodLength(trends?.labels);
  return Array.from({ length: count }, () => null);
}

function getNoteZones(player, trends, values) {
  const stored = trends?.series?.[String(player.id)]?.note_zone;
  return values.map((value, index) => {
    const fromApi = Array.isArray(stored) ? stored[index] : null;
    const isLatest = index === values.length - 1;
    if (isLatest && player?.zone) return player.zone;
    return resolveFoldedZone(fromApi, player, value);
  });
}

function lineSegments(points) {
  const segments = [];
  let current = [];
  points.forEach((point) => {
    if (!point) {
      if (current.length) segments.push(current);
      current = [];
    } else {
      current.push(point);
    }
  });
  if (current.length) segments.push(current);
  return segments;
}

function metricScale(metric) {
  if (metric.folded) return { min: 0, max: 10, digits: 0 };
  if (metric.unit === "PT") return { min: 0, max: 2, digits: 1 };
  return { min: 0, max: 200, digits: 0 };
}

function metricRef(metric) {
  if (metric.unit === "%") return 100;
  if (metric.unit === "PT") return 1;
  return null;
}

function formatMetricValue(value, metric) {
  if (!Number.isFinite(value)) return "—";
  if (metric.unit === "%") return `${formatScore(value, 0)}%`;
  if (metric.unit === "PT") return `${formatScore(value, 2)} PT`;
  return formatScore(value, 1);
}

function ZoneBadge({ zone, t }) {
  const color = V2_ZONE_META[zone]?.color || "#8581bc";
  const Icon = zone === "optimal" ? Check : zone === "overload" || zone === "supraoptimal" ? ArrowUpRight : ArrowDown;
  return (
    <em
      className="inline-flex items-center gap-1 rounded-full border border-current bg-[rgba(8,5,40,0.35)] px-2 py-[3px] text-[10px] font-bold not-italic"
      style={{ color }}
    >
      <Icon size={11} strokeWidth={2.6} />
      {t(`v2.overview.${zone}`)}
    </em>
  );
}

function TrendChart({
  players,
  period,
  metric,
  hoveredId,
  compact = false,
  onHoverPlayer,
  trends,
}) {
  const { t } = useTranslation();
  const clipId = `v2-trend-${useId().replace(/:/g, "")}`;
  const [hoverIndex, setHoverIndex] = useState(null);
  const labels = trendLabels(period, t, trends?.labels);
  const folded = Boolean(metric.folded);
  const width = compact ? 420 : 860;
  const height = compact ? (folded ? 196 : 176) : 292;
  const pad = compact
    ? { l: 36, r: folded ? 86 : 16, t: 12, b: 28 }
    : { l: 44, r: folded ? 126 : 72, t: 16, b: 34 };
  const innerW = width - pad.l - pad.r;
  const innerH = height - pad.t - pad.b;
  const { min, max, digits } = metricScale(metric);
  const reference = folded ? null : metricRef(metric);
  const ticks = [0, 0.25, 0.5, 0.75, 1];

  const series = players.map((player) => {
    const values = getSerie(player, period, metric, trends);
    return {
      player,
      values,
      zones: folded ? getNoteZones(player, trends, values) : [],
    };
  });

  const point = (index, count, value, zone) => {
    const x = pad.l + (index / Math.max(count - 1, 1)) * innerW;
    const y = folded
      ? pad.t + (1 - foldedLeftPercent(value, zone) / 100) * innerH
      : pad.t + (1 - (value - min) / Math.max(max - min, 1)) * innerH;
    return { x, y };
  };

  const foldedY = (at) => pad.t + (1 - at / 100) * innerH;

  const updateHover = (event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const x = ((event.clientX - rect.left) / rect.width) * width;
    const ratio = (x - pad.l) / innerW;
    const index = Math.round(ratio * (labels.length - 1));
    if (index < 0 || index >= labels.length) {
      setHoverIndex(null);
      return;
    }
    setHoverIndex(index);
  };

  const hoverX = hoverIndex == null ? null : pad.l + (hoverIndex / Math.max(labels.length - 1, 1)) * innerW;

  return (
    <div className="relative">
      <svg
        className={`h-auto w-full ${compact ? "" : "min-h-[240px]"}`}
        viewBox={`0 0 ${width} ${height}`}
        role="img"
        onMouseMove={updateHover}
        onMouseLeave={() => {
          setHoverIndex(null);
          onHoverPlayer?.(null);
        }}
      >
        <defs>
          <clipPath id={clipId}>
            <rect x={pad.l} y={pad.t} width={innerW} height={innerH} rx="8" />
          </clipPath>
        </defs>

        {folded ? (
          <g>
            <rect
              x={pad.l}
              y={pad.t}
              width={innerW}
              height={innerH}
              rx="8"
              fill="#0b0730"
            />
            {FOLDED_ZONE_BANDS.map((band) => {
              const y = foldedY(band.end);
              const h = Math.max(foldedY(band.start) - y, 0);
              return (
                <g key={band.key}>
                  <rect x={pad.l} y={y} width={innerW} height={h} fill={band.fill} />
                  <line
                    x1={pad.l}
                    x2={pad.l + innerW}
                    y1={y}
                    y2={y}
                    stroke="rgba(243,242,255,0.12)"
                    strokeDasharray="3 5"
                  />
                </g>
              );
            })}
            <rect
              x={pad.l}
              y={pad.t}
              width={innerW}
              height={innerH}
              rx="8"
              fill="none"
              stroke="rgba(98,94,166,0.28)"
            />
            {FOLDED_AXIS_TICKS.map((tick, index) => {
              const y = foldedY(tick.at);
              return (
                <g key={`${tick.label}-${index}`}>
                  <line
                    x1={pad.l}
                    x2={pad.l + innerW}
                    y1={y}
                    y2={y}
                    stroke="rgba(98,94,166,0.22)"
                  />
                  <text x={pad.l - 8} y={y + 3} textAnchor="end" fill="#8581bc" fontSize="10">
                    {tick.label}
                  </text>
                </g>
              );
            })}
            {FOLDED_ZONE_BANDS.map((band) => {
              const y = foldedY((band.start + band.end) / 2);
              return (
                <text
                  key={`${band.key}-label`}
                  x={pad.l + innerW + 10}
                  y={y + 3}
                  fill={V2_ZONE_META[band.key]?.text || "#8581bc"}
                  fontSize={compact ? 9 : 11}
                  fontWeight="700"
                >
                  {t(`v2.overview.${band.key}`)}
                </text>
              );
            })}
          </g>
        ) : (
          ticks.map((ratio) => {
            const y = pad.t + innerH * (1 - ratio);
            const value = min + (max - min) * ratio;
            return (
              <g key={ratio}>
                <line x1={pad.l} x2={width - pad.r} y1={y} y2={y} stroke="rgba(98,94,166,0.28)" />
                <text x={pad.l - 8} y={y + 3} textAnchor="end" fill="#8581bc" fontSize="10">
                  {formatScore(value, digits)}
                </text>
              </g>
            );
          })
        )}

        {reference != null && (
          <g>
            <line
              x1={pad.l}
              x2={width - pad.r}
              y1={pad.t + (1 - (reference - min) / (max - min)) * innerH}
              y2={pad.t + (1 - (reference - min) / (max - min)) * innerH}
              stroke="rgba(171,167,217,0.7)"
              strokeDasharray="4 5"
            />
            {!compact && (
              <text
                x={width - pad.r + 8}
                y={pad.t + (1 - (reference - min) / (max - min)) * innerH + 3}
                fill="#9b96c9"
                fontSize="10"
              >
                {metric.unit === "%"
                  ? t("v2.overview.trends.ref_percent", { value: reference })
                  : t("v2.overview.trends.ref_pt", { value: reference })}
              </text>
            )}
          </g>
        )}

        {hoverX != null && (
          <line x1={hoverX} x2={hoverX} y1={pad.t} y2={pad.t + innerH} stroke="rgba(243,242,255,0.22)" />
        )}

        <g clipPath={`url(#${clipId})`}>
          {series.map(({ player, values, zones }) => {
            const emphasized = !hoveredId || hoveredId === player.id;
            const pts = values.map((value, index) => (
              Number.isFinite(value) ? point(index, values.length, value, zones[index]) : null
            ));
            const segments = lineSegments(pts);
            if (!segments.length) return null;
            return (
              <g
                key={player.id}
                opacity={emphasized ? 1 : 0.18}
                onMouseEnter={() => onHoverPlayer?.(player.id)}
                style={{ cursor: "pointer" }}
              >
                {segments.map((segment, segmentIndex) => (
                  <polyline
                    key={`${player.id}-seg-${segmentIndex}`}
                    fill="none"
                    stroke={emphasized ? player.color : "#6f6aa8"}
                    strokeWidth={emphasized && hoveredId === player.id ? 3 : 2.2}
                    strokeLinejoin="round"
                    strokeLinecap="round"
                    points={segment.map((item) => `${item.x},${item.y}`).join(" ")}
                  />
                ))}
                {pts.map((item, index) => (
                  item ? (
                    <circle
                      key={`${player.id}-${index}`}
                      cx={item.x}
                      cy={item.y}
                      r={emphasized && hoveredId === player.id ? 4 : 3}
                      fill={emphasized ? player.color : "#6f6aa8"}
                    />
                  ) : null
                ))}
              </g>
            );
          })}
        </g>

        {labels.map((label, index) => (
          <text
            key={`${label}-${index}`}
            x={pad.l + (index / Math.max(labels.length - 1, 1)) * innerW}
            y={height - 8}
            textAnchor="middle"
            fill="#8581bc"
            fontSize="10"
          >
            {compact && index % 2 === 1 ? "" : label}
          </text>
        ))}
      </svg>

      {hoverIndex != null && !compact && (
        <div
          className="pointer-events-none absolute z-10 min-w-[160px] rounded-lg border border-[rgba(98,94,166,0.45)] bg-[#19144e] px-2.5 py-2 shadow-[0_12px_28px_rgba(2,0,16,0.45)]"
          style={{
            left: `min(${(hoverX / width) * 100}%, calc(100% - 180px))`,
            top: 18,
          }}
        >
          <p className="mb-1.5 text-[10px] font-bold uppercase tracking-[0.06em] text-[#9b96c9]">{labels[hoverIndex]}</p>
          <ul className="m-0 list-none space-y-1 p-0">
            {series.map(({ player, values }) => (
              <li
                key={player.id}
                className={`flex items-center justify-between gap-3 text-[11px] ${
                  hoveredId && hoveredId !== player.id ? "opacity-40" : "text-[#f4f2ff]"
                }`}
              >
                <span className="inline-flex items-center gap-1.5">
                  <i className="h-1.5 w-3 rounded-sm" style={{ background: player.color }} />
                  {player.name}
                </span>
                <strong>{formatMetricValue(values[hoverIndex], metric)}</strong>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export default function V2RecentTrends({
  players,
  selectedPositions,
  trends,
  period: periodProp,
  onPeriodChange,
  clubId,
  loading = false,
}) {
  const { t } = useTranslation();
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState([]);
  const [localPeriod, setLocalPeriod] = useState("week");
  const period = periodProp || localPeriod;
  const setPeriod = onPeriodChange || setLocalPeriod;
  const [metricId, setMetricId] = useState("note");
  const [mode, setMode] = useState("overlay");
  const [highLoad, setHighLoad] = useState(false);
  const [posChip, setPosChip] = useState("");
  const [hoveredId, setHoveredId] = useState(null);
  const initialized = useRef(false);
  const metric = TREND_METRICS.find((item) => item.id === metricId) || TREND_METRICS[0];

  useEffect(() => {
    if (!players.length) return;
    setSelected((current) => {
      const visibleIds = new Set(players.map((player) => player.id));
      const kept = current.filter((id) => visibleIds.has(id));
      if (initialized.current) return kept;
      initialized.current = true;
      return kept.length ? kept : players.slice(0, 3).map((player) => player.id);
    });
  }, [players]);

  const visible = useMemo(() => {
    return players.filter((player) => {
      const byFilter = matchesPosition(player, selectedPositions);
      const byChip = !posChip || player.position === posChip;
      const byQuery = player.name.toLowerCase().includes(query.toLowerCase());
      return byFilter && byChip && byQuery;
    });
  }, [players, posChip, query, selectedPositions]);

  const selectedPlayers = useMemo(
    () => players.filter((player) => selected.includes(player.id)),
    [players, selected]
  );

  const toggle = (id) => {
    setSelected((current) => (
      current.includes(id) ? current.filter((item) => item !== id) : [...current, id]
    ));
  };

  const setHover = (id) => {
    if (id && !selected.includes(id)) return;
    setHoveredId(id);
  };

  const hasTrends = Boolean(trends?.labels?.length);

  return (
    <V2BusyFrame loading={loading} label={t("v2.overview.fetching")}>
    <div className="grid grid-cols-[280px_minmax(0,1fr)] gap-5 max-[1180px]:grid-cols-1" data-kind="int">
      <aside className="rounded-lg border border-[rgba(98,94,166,0.35)] bg-[rgba(11,7,53,0.45)] p-3">
        <div className="mb-2 flex items-center justify-between gap-1.5 text-[11px] font-bold uppercase tracking-[0.06em] text-[#9b96c9]">
          <span>{t("v2.overview.trends.players")}</span>
          <strong className="font-semibold normal-case tracking-normal text-[#c9c5f5]">
            {selected.length} / {visible.length}
          </strong>
        </div>
        <label className="mb-2 flex h-[34px] items-center gap-[9px] rounded-[5px] border border-[rgba(98,94,166,0.48)] bg-[rgba(17,12,61,0.6)] px-[11px] text-v2-subtle focus-within:border-v2-cyan">
          <Search size={13} />
          <input
            className="w-full min-w-0 border-0 bg-transparent text-[12px] text-v2-foreground outline-none placeholder:text-[#7772ad]"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={t("v2.overview.trends.search")}
          />
        </label>
        <div className="mb-1.5 flex flex-wrap items-center justify-between gap-1.5 text-[11px]">
          <button
            type="button"
            className="border-0 bg-transparent p-0 font-semibold text-[#c9c5f5] hover:text-white"
            onClick={() => setSelected(visible.map((player) => player.id))}
          >
            {t("v2.overview.trends.select_all")}
          </button>
          <button
            type="button"
            className="border-0 bg-transparent p-0 font-semibold text-[#34cbff] hover:text-white"
            onClick={() => setSelected([])}
          >
            {t("v2.overview.trends.clear")}
          </button>
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          <button
            type="button"
            className={`rounded-full border px-2.5 py-1 text-[11px] ${
              !posChip
                ? "border-transparent bg-[#2a2478] text-[#f7f5ff]"
                : "border-[rgba(98,94,166,0.45)] bg-transparent text-[#aaa5d7] hover:text-[#f4f2ff]"
            }`}
            onClick={() => setPosChip("")}
          >
            {t("v2.overview.trends.all")}
          </button>
          {V2_POSITIONS.map((position) => (
            <button
              key={position.id}
              type="button"
              className={`rounded-full border px-2.5 py-1 text-[11px] ${
                posChip === position.id
                  ? "border-transparent bg-[#2a2478] text-[#f7f5ff]"
                  : "border-[rgba(98,94,166,0.45)] bg-transparent text-[#aaa5d7] hover:text-[#f4f2ff]"
              }`}
              onClick={() => setPosChip(posChip === position.id ? "" : position.id)}
            >
              {t(position.labelKey)}
            </button>
          ))}
        </div>
        <div className="mt-3 mb-1 grid grid-cols-[1fr_auto] px-1 text-[10px] font-bold uppercase tracking-[0.06em] text-[#6f6aa8]">
          <span>{t("v2.overview.trends.player")}</span>
          <span>{t("v2.overview.trainability")}</span>
        </div>
        <ul className="m-0 max-h-[390px] list-none overflow-auto p-0">
          {loading && visible.length === 0
            ? Array.from({ length: 6 }, (_, index) => (
              <li key={`sk-${index}`} className="flex items-center gap-2 px-1.5 py-2">
                <V2Skeleton className="h-3.5 w-3.5 rounded-[3px]" />
                <V2Skeleton className="h-[22px] w-[22px] rounded-full" />
                <div className="min-w-0 flex-1">
                  <V2Skeleton className="h-2.5 w-28" />
                  <V2Skeleton className="mt-1.5 h-2 w-16" />
                </div>
                <V2Skeleton className="h-2.5 w-8" />
              </li>
            ))
            : visible.map((player) => {
            const on = selected.includes(player.id);
            const active = hoveredId === player.id;
            return (
              <li key={player.id}>
                <button
                  type="button"
                  className={`grid min-h-[42px] w-full grid-cols-[16px_8px_auto_1fr_auto] items-center gap-[7px] rounded-[6px] border-0 px-1.5 py-1 text-left text-[12px] text-[#f1efff] ${
                    on ? "bg-[#17124f]" : "bg-transparent hover:bg-[rgba(98,94,166,0.14)]"
                  } ${active ? "ring-1 ring-[rgba(52,203,255,0.35)]" : ""}`}
                  onClick={() => toggle(player.id)}
                  onMouseEnter={() => setHover(player.id)}
                  onMouseLeave={() => setHoveredId(null)}
                >
                  <span
                    className={`grid h-3.5 w-3.5 place-items-center rounded-[3px] border ${
                      on ? "border-[#3b6bff] bg-[#3b6bff]" : "border-[#625ea6] bg-transparent"
                    }`}
                    aria-hidden="true"
                  >
                    {on && <Check size={9} strokeWidth={3} className="text-white" />}
                  </span>
                  <i
                    className="h-[2px] w-2 rounded-full"
                    style={{ background: on ? player.color : "transparent" }}
                  />
                  <V2PlayerAvatar
                    name={player.name}
                    color={player.color}
                    zone={player.zone}
                    size={22}
                    clubId={clubId}
                    playerId={player.id}
                  />
                  <span className="min-w-0">
                    <strong className="block truncate text-[12px] font-semibold">{player.name}</strong>
                    <small className="block text-[10px] text-[#8b86bc]">
                      {t(`v2.overview.positions.${String(player.position || "mil").toLowerCase()}`)}
                    </small>
                  </span>
                  <em className="text-[12px] font-bold not-italic" style={{ color: V2_ZONE_META[player.zone]?.text }}>
                    {formatScore(player.score)}
                  </em>
                </button>
              </li>
            );
          })}
        </ul>
      </aside>

      <div>
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <label className="v2-alert-setting inline-flex cursor-pointer items-center gap-2 text-[12px] text-[#d8d5ff]">
            <input
              type="checkbox"
              className="sr-only"
              checked={highLoad}
              onChange={(event) => setHighLoad(event.target.checked)}
            />
            <span className="v2-toggle" aria-hidden="true" />
            {t("v2.overview.trends.high_load")}
            <HelpCircle size={13} className="text-[#8b86bc]" title={t("v2.overview.trends.high_load_help")} />
          </label>
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex overflow-hidden rounded-[6px] border border-[rgba(98,94,166,0.55)]">
              <button
                type="button"
                className={`inline-flex min-h-8 items-center gap-1.5 border-0 px-3 text-[12px] ${
                  mode === "overlay" ? "bg-[rgba(35,30,98,0.95)] text-[#f4f2ff]" : "bg-transparent text-[#aaa5d7]"
                }`}
                onClick={() => setMode("overlay")}
              >
                <Layers size={13} />
                {t("v2.overview.trends.overlay")}
              </button>
              <button
                type="button"
                className={`inline-flex min-h-8 items-center gap-1.5 border-0 border-l border-[rgba(98,94,166,0.35)] px-3 text-[12px] ${
                  mode === "multi" ? "bg-[rgba(35,30,98,0.95)] text-[#f4f2ff]" : "bg-transparent text-[#aaa5d7]"
                }`}
                onClick={() => setMode("multi")}
              >
                <LayoutGrid size={13} />
                {t("v2.overview.trends.multi")}
              </button>
            </div>
            <div className="flex overflow-hidden rounded-[6px] border border-[rgba(98,94,166,0.55)]">
              {["week", "month", "season"].map((item) => (
                <button
                  key={item}
                  type="button"
                  className={`min-h-8 border-0 px-3 text-[12px] ${
                    period === item ? "bg-[rgba(35,30,98,0.95)] text-[#f4f2ff]" : "bg-transparent text-[#aaa5d7]"
                  }`}
                  onClick={() => setPeriod(item)}
                >
                  {t(`v2.overview.trends.${item}`)}
                </button>
              ))}
            </div>
          </div>
        </div>

        {highLoad && <p className="mb-2 text-[12px] text-[#8edbff]">{t("v2.overview.trends.high_load_help")}</p>}

        {selectedPlayers.length > 0 && (
          <div className="mb-3 flex flex-wrap items-center gap-2 text-[12px] text-[#9b96c9]">
            <span>{t("v2.overview.trends.shown_for")} :</span>
            {selectedPlayers.map((player) => (
              <span
                key={player.id}
                className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-1 ${
                  hoveredId === player.id
                    ? "border-[rgba(52,203,255,0.55)] bg-[rgba(52,203,255,0.1)] text-[#f4f2ff]"
                    : "border-[rgba(98,94,166,0.4)] bg-[rgba(17,12,61,0.45)] text-[#d8d5ff]"
                }`}
                onMouseEnter={() => setHover(player.id)}
                onMouseLeave={() => setHoveredId(null)}
              >
                <i className="h-[2px] w-3 rounded-full" style={{ background: player.color }} />
                {player.name}
                <button
                  type="button"
                  className="grid h-4 w-4 place-items-center rounded-full border-0 bg-transparent text-[#9b96c9] hover:text-white"
                  onClick={() => toggle(player.id)}
                  aria-label={t("v2.overview.trends.remove_player", { name: player.name })}
                >
                  <X size={11} />
                </button>
              </span>
            ))}
          </div>
        )}

        <div className="mb-3 flex flex-wrap items-end gap-x-4 gap-y-2 border-b border-[rgba(98,94,166,0.28)]">
          {TREND_METRICS.map((item) => {
            const active = metricId === item.id;
            return (
              <button
                key={item.id}
                type="button"
                className={`border-0 bg-transparent px-0 pb-2 text-left ${
                  active ? "text-[#f4f2ff]" : "text-[#8b86bc] hover:text-[#d8d5ff]"
                }`}
                onClick={() => setMetricId(item.id)}
              >
                <span className="block text-[12px] font-semibold">{t(item.titleKey)}</span>
                <span className="block text-[10px] opacity-70">{item.unit}</span>
                <span className={`mt-1 block h-[2px] rounded-full ${active ? "bg-[#34cbff]" : "bg-transparent"}`} />
              </button>
            );
          })}
        </div>

        {selectedPlayers.length === 0 && !loading ? (
          <p className="grid min-h-[220px] place-items-center text-center text-[13px] text-[#8b86bc]">
            {t("v2.overview.trends.empty")}
          </p>
        ) : loading && !hasTrends ? (
          <div className="grid min-h-[220px] gap-3 py-2" aria-hidden="true">
            <V2Skeleton className="h-[180px] w-full rounded-lg" />
            <div className="flex gap-2">
              <V2Skeleton className="h-2 w-16" />
              <V2Skeleton className="h-2 w-20" />
              <V2Skeleton className="h-2 w-14" />
            </div>
          </div>
        ) : !hasTrends ? (
          <p className="grid min-h-[220px] place-items-center text-center text-[13px] text-[#8b86bc]">
            {t("v2.overview.load_empty")}
          </p>
        ) : mode === "overlay" ? (
          <TrendChart
            players={selectedPlayers}
            period={period}
            metric={metric}
            hoveredId={hoveredId}
            onHoverPlayer={setHover}
            trends={trends}
          />
        ) : (
          <div className="grid grid-cols-3 gap-3 max-[1280px]:grid-cols-2 max-[720px]:grid-cols-1">
            {selectedPlayers.map((player) => (
              <article
                key={player.id}
                className={`rounded-xl border bg-[rgba(11,7,53,0.45)] p-3 ${
                  hoveredId === player.id
                    ? "border-[rgba(52,203,255,0.5)]"
                    : "border-[rgba(98,94,166,0.35)]"
                }`}
                onMouseEnter={() => setHover(player.id)}
                onMouseLeave={() => setHoveredId(null)}
              >
                <header className="mb-2 flex items-center justify-between gap-2">
                  <strong className="inline-flex items-center gap-1.5 text-[13px] text-[#f4f2ff]">
                    <i className="h-[2px] w-3 rounded-full" style={{ background: player.color }} />
                    {player.name}
                  </strong>
                  <ZoneBadge zone={player.zone} t={t} />
                </header>
                <TrendChart
                  players={[player]}
                  period={period}
                  metric={metric}
                  hoveredId={hoveredId}
                  compact
                  trends={trends}
                />
              </article>
            ))}
          </div>
        )}
      </div>
    </div>
    </V2BusyFrame>
  );
}
